import React, { useState, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, NamedLink } from '../../../components';
import { post } from '../../../util/api';
import { RECAPTCHA_SITE_KEY, isRecaptchaEnabled } from '../../../util/recaptcha';

import css from './LoginForm.module.css';
import { useParams } from 'react-router-dom';
const BOT_REDIRECT_URL = 'https://www.google.com/';

/**
 * PERFORMANCE: Memoized password recovery link to prevent re-renders
 */
const PasswordRecoveryLink = memo(function PasswordRecoveryLink() {
  return (
    <NamedLink name="PasswordRecoveryPage" className={css.recoveryLink}>
      <FormattedMessage id="LoginForm.forgotPassword" />
    </NamedLink>
  );
});

/** Form UI shared by both captcha and no-captcha flows */
const LoginFormFields = props => {
  const { intl, onSubmit, captchaError, ...restProps } = props;

  // PERF: Memoize validation messages to prevent recalculation on each render
  const validationMessages = useMemo(() => ({
    emailLabel: intl.formatMessage({ id: 'LoginForm.emailLabel' }),
    emailPlaceholder: intl.formatMessage({ id: 'LoginForm.emailPlaceholder' }),
    emailRequiredMessage: intl.formatMessage({ id: 'LoginForm.emailRequired' }),
    emailInvalidMessage: intl.formatMessage({ id: 'LoginForm.emailInvalid' }),
    passwordLabel: intl.formatMessage({ id: 'LoginForm.passwordLabel' }),
    passwordPlaceholder: intl.formatMessage({ id: 'LoginForm.passwordPlaceholder' }),
    passwordRequiredMessage: intl.formatMessage({ id: 'LoginForm.passwordRequired' }),
  }), [intl]);

  // PERF: Memoize validators to prevent recreation on each render
  const emailValidators = useMemo(() => {
    const emailRequired = validators.required(validationMessages.emailRequiredMessage);
    const emailValid = validators.emailFormatValid(validationMessages.emailInvalidMessage);
    return validators.composeValidators(emailRequired, emailValid);
  }, [validationMessages.emailRequiredMessage, validationMessages.emailInvalidMessage]);

  const passwordValidator = useMemo(() => {
    return validators.requiredStringNoTrim(validationMessages.passwordRequiredMessage);
  }, [validationMessages.passwordRequiredMessage]);

  return (
    <FinalForm
      {...restProps}
      onSubmit={onSubmit}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit: formHandleSubmit,
          inProgress,
          invalid,
        } = fieldRenderProps;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;

        return (
          <Form className={classes} onSubmit={formHandleSubmit}>
            <div>
              <FieldTextInput
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={validationMessages.emailLabel}
                placeholder={validationMessages.emailPlaceholder}
                validate={emailValidators}
              />
              <FieldTextInput
                className={css.password}
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="current-password"
                label={validationMessages.passwordLabel}
                placeholder={validationMessages.passwordPlaceholder}
                validate={passwordValidator}
              />
            </div>

            {captchaError ? <p className={css.captchaError}>{captchaError}</p> : null}

            <div className={css.bottomWrapper}>
              <p className={css.bottomWrapperText}>
                <span className={css.recoveryLinkInfo}>
                  <FormattedMessage
                    id="LoginForm.forgotPasswordInfo"
                    values={{ passwordRecoveryLink: <PasswordRecoveryLink /> }}
                  />
                </span>
              </p>
              <PrimaryButton
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                data-elbaction={'click:click loginbutton'}
              >
                <FormattedMessage id="LoginForm.logIn" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

/** Wrapper that adds reCAPTCHA verification (only rendered when provider is mounted) */
const LoginFormWithCaptcha = props => {
  const { intl, onSubmit, ...restProps } = props;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isVerifying, setIsVerifying] = useState(false);
  const [captchaError, setCaptchaError] = useState(null);

  const verifyRecaptchaToken = useCallback(async (token, action) => {
    try {
      const response = await post(`/api/recaptcha/verify`, { token, action });
      return response;
    } catch (error) {
      console.error('Error verifying reCAPTCHA:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const handleSubmitWithCaptcha = useCallback(
    async values => {
      if (executeRecaptcha) {
        setIsVerifying(true);
        setCaptchaError(null);
        try {
          const token = await executeRecaptcha('login');
          const verification = await verifyRecaptchaToken(token, 'login');
          if (!verification.success) {
            setIsVerifying(false);
            setCaptchaError(intl.formatMessage({ id: 'LoginForm.captchaVerificationFailed' }));
            return;
          }
          if (!verification.isHuman) {
            console.log('Bot detected! Score:', verification.score);
            window.location.href = BOT_REDIRECT_URL;
            return;
          }
          setIsVerifying(false);
          return onSubmit(values);
        } catch (error) {
          console.error('reCAPTCHA error:', error);
          setIsVerifying(false);
          setCaptchaError(intl.formatMessage({ id: 'LoginForm.captchaError' }));
          return;
        }
      }
      return onSubmit(values);
    },
    [executeRecaptcha, onSubmit, intl, verifyRecaptchaToken]
  );

  return <LoginFormFields {...restProps} intl={intl} onSubmit={handleSubmitWithCaptcha} captchaError={captchaError} />;
};

const LoginFormComponent = props => {
  const { lang: currentLanguage } = useParams();
  const captchaRequired = !!RECAPTCHA_SITE_KEY && isRecaptchaEnabled();
  if (captchaRequired) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        language={currentLanguage}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: 'head',
          id: 'recaptcha-login',
        }}
        useEnterprise={false}
      >
        <LoginFormWithCaptcha {...props} />
      </GoogleReCaptchaProvider>
    );
  }
  return <LoginFormFields {...props} captchaError={null} />;
};

LoginFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  form: null,
  inProgress: false,
};

const { string, bool } = PropTypes;

LoginFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  form: string,
  inProgress: bool,
  intl: intlShape.isRequired,
};

const LoginForm = compose(injectIntl)(LoginFormComponent);
LoginForm.displayName = 'LoginForm';

export default LoginForm;
